import React, { useRef, useCallback, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { privateFetch } from '../../utils/apiHelper';
import { useAccountContext } from '../../contexts/AccountContext';
import slugify from 'react-slugify';
import { useToastContext } from '../../contexts/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { CONSTANT } from '../../utils/constants';

// Importe le module de redimensionnement moderne
import QuillResizeImage from 'quill-resize-image';

// Enregistre le module sous le nom "resize"
Quill.register('modules/resize', QuillResizeImage);

function RichTextEditor({ value, onChange, height, readOnly = false, acceptImage, acceptHeaders }) {
  const reactQuillRef = useRef(null);
  const { accountContext } = useAccountContext();
  const { showToast } = useToastContext();
  const [uploading, setUploading] = useState(false);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      if (file.type === 'image/svg+xml') {
        showToast('warn', 'Les fichiers SVG ne sont pas autorisés.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        showToast('warn', "Le fichier sélectionné n'est pas une image.");
        return;
      }

      if (file.size > CONSTANT.maxImageSize) {
        showToast('warn', 'La taille du fichier est trop importante.');
        return;
      }

      const imageForm = new FormData();
      imageForm.append('name', slugify(file.name));
      imageForm.append('file', file);

      setUploading(true);
      try {
        const res = await privateFetch(
          'POST',
          'resources',
          `/cu/${accountContext.id}/image/`,
          "L'image a bien été ajoutée",
          imageForm,
          false,
          true
        );

        if (res?.file) {
          const editor = reactQuillRef.current?.getEditor();
          const range = editor?.getSelection();
          if (range) {
            editor.insertEmbed(range.index, 'image', res.file);
            editor.insertText(range.index + 1, '\n');
            editor.setSelection(range.index + 2, 0);
          }
        } else {
          showToast('error', 'L’image n’a pas pu être ajoutée.');
        }
      } catch (error) {
        console.error('Erreur lors de l’upload :', error);
        showToast('error', 'L’image n’a pas pu être ajoutée.');
      } finally {
        setUploading(false);
      }
    };
  }, [accountContext.id, showToast]);

  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        acceptImage ? ['link', 'image'] : ['link'],
        acceptHeaders ? [{ header: [2, 3, false] }] : [],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    resize: {
      locale: {
        floatLeft: 'Gauche',
        floatRight: 'Droite',
        center: 'Centrer',
        restore: 'Annuler',
        altTip: 'Maintenir Alt pour verrouiller les proportions !',
        inputTip: 'Appuyez sur Entrée pour appliquer la modification !'
      }
    }
  };

  return (
    <div className="relative mb-12 h-full">
      {uploading && (
        <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center h-full w-full">
          <ProgressSpinner style={{ width: '42px', height: '42px' }} />
        </div>
      )}
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        style={{ height: height || '24rem' }}
        modules={modules}
        readOnly={readOnly}
        ref={reactQuillRef}
        placeholder="Entrez votre contenu..."
      />
    </div>
  );
}

export default RichTextEditor;
